import { useMemo, useState } from "react";
import { Check, Type } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { BrandIcon, BrandLucideGlyph } from "@/components/shell/BrandIcon";
import {
  BRAND_COLOR_SWATCHES,
  BRAND_ICON_KEYS,
  normalizeBrandColor,
  type BrandIconKey,
} from "@/lib/brand-icons";
import { cn } from "@/lib/utils";

export type AppearanceValue = {
  iconKey: string | null;
  color: string | null;
};

/** @deprecated Prefer AppearanceValue — alias kept for brand call sites. */
export type BrandAppearanceValue = AppearanceValue;

type AppearanceFieldsProps = {
  name: string;
  value: AppearanceValue;
  onChange: (next: AppearanceValue) => void;
  /** Fallback preview label when name is empty. */
  emptyNameLabel?: string;
  /** Short help under the icon label. */
  description?: string;
  /** When true, Popover uses modal mode (needed inside Dialog). */
  modal?: boolean;
  idPrefix?: string;
};

/**
 * Icon + color picker (Account / Brand).
 * Pattern: shadcn Popover + Command searchable Lucide list (curated subset).
 */
export function AppearanceFields({
  name,
  value,
  onChange,
  emptyNameLabel = "名称",
  description = "默认用名称首字；也可从 Lucide 精选图标中选择（可搜索）。",
  modal = true,
  idPrefix = "appearance",
}: AppearanceFieldsProps) {
  const [iconOpen, setIconOpen] = useState(false);
  const previewName = name.trim() || emptyNameLabel;
  const selectedKey = value.iconKey;

  const iconLabel = useMemo(() => {
    if (!selectedKey) return "首字标识（默认）";
    return selectedKey;
  }, [selectedKey]);

  function setIconKey(next: string | null) {
    onChange({ ...value, iconKey: next });
    setIconOpen(false);
  }

  function setColor(next: string | null) {
    onChange({ ...value, color: next ? normalizeBrandColor(next) : null });
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <BrandIcon
          name={previewName}
          iconKey={value.iconKey}
          color={value.color}
          size="lg"
        />
        <div className="min-w-0 flex-1 space-y-1">
          <Label htmlFor={`${idPrefix}-icon`}>侧栏图标</Label>
          <p className="text-xs text-muted-foreground">{description}</p>
        </div>
      </div>

      <Popover open={iconOpen} onOpenChange={setIconOpen} modal={modal}>
        <PopoverTrigger asChild>
          <Button
            id={`${idPrefix}-icon`}
            type="button"
            variant="outline"
            className="w-full justify-start gap-2 font-normal"
          >
            {selectedKey ? (
              <BrandLucideGlyph iconKey={selectedKey} className="size-4" />
            ) : (
              <Type className="size-4" />
            )}
            <span className="truncate">{iconLabel}</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80 p-0" align="start">
          <Command>
            <CommandInput placeholder="搜索图标…" />
            <CommandList className="max-h-56">
              <CommandEmpty>没有匹配的图标</CommandEmpty>
              <CommandGroup heading="显示方式">
                <CommandItem
                  value="首字标识 default initial"
                  onSelect={() => setIconKey(null)}
                >
                  <Type className="size-4" />
                  <span>首字标识（默认）</span>
                  {!selectedKey ? <Check className="ml-auto size-4" /> : null}
                </CommandItem>
              </CommandGroup>
              <CommandGroup heading="图标">
                {BRAND_ICON_KEYS.map((key) => (
                  <CommandItem
                    key={key}
                    value={key}
                    onSelect={() => setIconKey(key as BrandIconKey)}
                  >
                    <BrandLucideGlyph iconKey={key} className="size-4" />
                    <span>{key}</span>
                    {selectedKey === key ? <Check className="ml-auto size-4" /> : null}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      <div className="space-y-2">
        <Label>颜色</Label>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            title="自动颜色"
            aria-label="自动颜色"
            onClick={() => setColor(null)}
            className={cn(
              "flex size-7 items-center justify-center rounded-full border border-border text-[10px] text-muted-foreground",
              !value.color && "ring-2 ring-ring ring-offset-2 ring-offset-background",
            )}
          >
            自动
          </button>
          {BRAND_COLOR_SWATCHES.map((swatch) => {
            const active = value.color?.toUpperCase() === swatch;
            return (
              <button
                key={swatch}
                type="button"
                title={swatch}
                aria-label={`颜色 ${swatch}`}
                onClick={() => setColor(swatch)}
                className={cn(
                  "size-7 rounded-full border border-black/5 dark:border-white/10",
                  active && "ring-2 ring-ring ring-offset-2 ring-offset-background",
                )}
                style={{ backgroundColor: swatch }}
              />
            );
          })}
          <label
            className={cn(
              "relative flex size-7 cursor-pointer items-center justify-center overflow-hidden rounded-full border border-dashed border-border",
              value.color &&
                !(BRAND_COLOR_SWATCHES as readonly string[]).includes(value.color) &&
                "ring-2 ring-ring ring-offset-2 ring-offset-background",
            )}
            title="自定义颜色"
          >
            <span className="pointer-events-none text-[10px] text-muted-foreground">+</span>
            <input
              type="color"
              aria-label="自定义颜色"
              className="absolute inset-0 cursor-pointer opacity-0"
              value={value.color ?? "#64748B"}
              onChange={(event) => setColor(event.target.value)}
            />
          </label>
        </div>
      </div>
    </div>
  );
}

/** Brand-flavored wrapper around AppearanceFields. */
export function BrandAppearanceFields({
  name,
  value,
  onChange,
  modal = true,
  idPrefix = "brand-appearance",
}: Omit<AppearanceFieldsProps, "emptyNameLabel" | "description">) {
  return (
    <AppearanceFields
      name={name}
      value={value}
      onChange={onChange}
      emptyNameLabel="品牌"
      description="默认用品牌名首字；也可从 Lucide 精选图标中选择（可搜索）。"
      modal={modal}
      idPrefix={idPrefix}
    />
  );
}
